<template>
  <div class="roles-table-container">
    <div v-if="loading" class="loading-spinner d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <p>{{ error }}</p>
    </div>
    
    <div v-else>
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th @click="sortBy('id')" class="sortable">
                ID
                <i class="fas" :class="getSortIcon('id')"></i>
              </th>
              <th @click="sortBy('name')" class="sortable">
                Name
                <i class="fas" :class="getSortIcon('name')"></i>
              </th>
              <th @click="sortBy('display_name')" class="sortable">
                Display Name
                <i class="fas" :class="getSortIcon('display_name')"></i>
              </th>
              <th>Description</th>
              <th>Parent Role</th>
              <th @click="sortBy('created_at')" class="sortable">
                Created At
                <i class="fas" :class="getSortIcon('created_at')"></i>
              </th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="role in filteredRoles" :key="role.id">
              <td>{{ role.id }}</td>
              <td>{{ role.name }}</td>
              <td>{{ role.display_name || '-' }}</td>
              <td>{{ role.description || '-' }}</td>
              <td>
                <span v-if="role.parent_id" class="badge bg-info">
                  {{ getParentRoleName(role.parent_id) }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>{{ formatDate(role.created_at) }}</td>
              <td class="text-end">
                <div class="btn-group">
                  <button class="btn btn-sm btn-outline-secondary" @click="openEditModal(role)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(role)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Create/Edit Role Modal -->
      <div v-if="showRoleModal" class="modal show d-block" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEditing ? 'Edit Role' : 'Create New Role' }}</h5>
              <button type="button" class="btn-close" @click="closeRoleModal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="submitRoleForm">
                <div class="mb-3">
                  <label for="name" class="form-label">Name</label>
                  <input
                    type="text"
                    class="form-control"
                    id="name"
                    v-model="roleForm.name"
                    required
                  >
                </div>
                <div class="mb-3">
                  <label for="display_name" class="form-label">Display Name</label>
                  <input
                    type="text"
                    class="form-control"
                    id="display_name"
                    v-model="roleForm.display_name"
                    required
                  >
                </div>
                <div class="mb-3">
                  <label for="description" class="form-label">Description</label>
                  <textarea
                    class="form-control"
                    id="description"
                    v-model="roleForm.description"
                    rows="3"
                  ></textarea>
                </div>
                <div class="mb-3">
                  <label class="form-label">Permissions</label>
                  <div v-if="loadingPermissions" class="text-center">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                      <span class="visually-hidden">Loading permissions...</span>
                    </div>
                  </div>
                  <div v-else class="permissions-list">
                    <div v-for="permission in availablePermissions" :key="permission.id" class="form-check">
                      <input
                        type="checkbox"
                        :id="`permission-${permission.id}`"
                        class="form-check-input"
                        :value="permission.id"
                        v-model="roleForm.permissions"
                      >
                      <label :for="`permission-${permission.id}`" class="form-check-label">
                        {{ permission.display_name || permission.name }} - {{ permission.description }}
                      </label>
                    </div>
                  </div>
                </div>
                <div class="d-flex justify-content-end">
                  <button type="button" class="btn btn-secondary me-2" @click="closeRoleModal">Cancel</button>
                  <button type="submit" class="btn btn-primary" :disabled="formSubmitting">
                    <span v-if="formSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    {{ isEditing ? 'Update' : 'Create' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Delete Confirmation Modal -->
      <div v-if="showDeleteModal" class="modal show d-block" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Confirm Delete</h5>
              <button type="button" class="btn-close" @click="showDeleteModal = false" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <p>Are you sure you want to delete the role "{{ roleToDelete?.display_name || roleToDelete?.name }}"?</p>
              <p class="text-danger">This action cannot be undone.</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
              <button type="button" class="btn btn-danger" @click="deleteRole" :disabled="deleteSubmitting">
                <span v-if="deleteSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Modal Backdrop -->
      <div v-if="selectedRole || showRoleModal || showDeleteModal" class="modal-backdrop show"></div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import axios from 'axios';
import apiService from '@/services/api';

export default {
  name: 'RolesTable',
  components: {
  },
  setup() {
    const roles = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const selectedRole = ref(null);
    const showRoleModal = ref(false);
    const isEditing = ref(false);
    const formSubmitting = ref(false);
    const availablePermissions = ref([]);
    const loadingPermissions = ref(false);
    const showDeleteModal = ref(false);
    const roleToDelete = ref(null);
    const deleteSubmitting = ref(false);
    const expandedPermissionRoles = ref([]);
    const searchQuery = ref('');
    const sortField = ref('id');
    const sortDirection = ref('asc');
    
    const apiToken = localStorage.getItem('token');
    const apiBaseUrl = 'https://api.kewaar.com/api/admin';
    
    const roleForm = reactive({
      name: '',
      display_name: '',
      description: '',
      permissions: []
    });

    const fetchRoles = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const response = await apiService.roles.getAll();
        
        if (response.data && response.data.success) {
          roles.value = response.data.data;
        } else {
          error.value = 'Failed to load roles: Invalid response format';
        }
      } catch (err) {
        console.error('Error fetching roles:', err);
        error.value = `Failed to load roles: ${err.message || 'Unknown error'}`;
      } finally {
        loading.value = false;
      }
    };
    
    const fetchPermissions = async () => {
      loadingPermissions.value = true;
      
      try {
        const response = await axios.get(`${apiBaseUrl}/permissions`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${apiToken}`
          }
        });
        
        if (response.data && response.data.success) {
          availablePermissions.value = response.data.data;
        } else {
          console.error('Failed to load permissions: Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching permissions:', err);
      } finally {
        loadingPermissions.value = false;
      }
    };

    // const fetchRolePermissions = async (roleId) => {
    //   try {
    //     const response = await axios.get(`${apiBaseUrl}/roles1/${roleId}/permissions`, {
    //       headers: {
    //         'Accept': 'application/json',
    //         'Authorization': `Bearer ${apiToken}`
    //       }
    //     });
        
    //     if (response.data && response.data.success) {
    //       return response.data.data;
    //     } else {
    //       console.error('Failed to load role permissions: Invalid response format');
    //       return null;
    //     }
    //   } catch (err) {
    //     console.error(`Error fetching permissions for role ${roleId}:`, err);
    //     return null;
    //   }
    // };

    const togglePermissionView = (roleId) => {
      if (expandedPermissionRoles.value.includes(roleId)) {
        expandedPermissionRoles.value = expandedPermissionRoles.value.filter(id => id !== roleId);
      } else {
        expandedPermissionRoles.value.push(roleId);
      }
    };
    
    const openCreateModal = () => {
      resetRoleForm();
      isEditing.value = false;
      showRoleModal.value = true;
      fetchPermissions();
    };
    
    const openEditModal = (role) => {
      resetRoleForm();
      roleForm.name = role.name;
      roleForm.display_name = role.display_name || '';
      roleForm.description = role.description || '';
      roleForm.permissions = role.permissions ? role.permissions.map(p => p.id || p) : [];
      isEditing.value = true;
      showRoleModal.value = true;
      fetchPermissions();
    };
    
    const closeRoleModal = () => {
      showRoleModal.value = false;
      resetRoleForm();
    };
    
    const resetRoleForm = () => {
      roleForm.name = '';
      roleForm.display_name = '';
      roleForm.description = '';
      roleForm.permissions = [];
    };
    
    const submitRoleForm = async () => {
      formSubmitting.value = true;
      
      try {
        let response;
        const payload = {
          name: roleForm.name,
          display_name: roleForm.display_name,
          description: roleForm.description,
          permissions: roleForm.permissions
        };
        
        if (isEditing.value) {
          // Update existing role
          response = await axios.put(`${apiBaseUrl}/roles1/${selectedRole.value.id}`, payload, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${apiToken}`
            }
          });
        } else {
          // Create new role
          response = await axios.post(`${apiBaseUrl}/roles1`, payload, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${apiToken}`
            }
          });
        }
        
        if (response.data && response.data.success) {
          closeRoleModal();
          fetchRoles();
        } else {
          error.value = `Failed to ${isEditing.value ? 'update' : 'create'} role: Invalid response format`;
        }
      } catch (err) {
        console.error(`Error ${isEditing.value ? 'updating' : 'creating'} role:`, err);
        error.value = `Failed to ${isEditing.value ? 'update' : 'create'} role: ${err.message || 'Unknown error'}`;
      } finally {
        formSubmitting.value = false;
      }
    };
    
    const confirmDelete = (role) => {
      roleToDelete.value = role;
      showDeleteModal.value = true;
    };
    
    const deleteRole = async () => {
      if (!roleToDelete.value) return;
      
      deleteSubmitting.value = true;
      
      try {
        const response = await axios.delete(`${apiBaseUrl}/roles1/${roleToDelete.value.id}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${apiToken}`
          }
        });
        
        if (response.data && response.data.success) {
          showDeleteModal.value = false;
          roleToDelete.value = null;
          fetchRoles();
        } else {
          error.value = 'Failed to delete role: Invalid response format';
        }
      } catch (err) {
        console.error('Error deleting role:', err);
        error.value = `Failed to delete role: ${err.message || 'Unknown error'}`;
      } finally {
        deleteSubmitting.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    const filteredRoles = computed(() => {
      let result = [...roles.value];
      
      // Apply search filter
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(role => 
          role.name.toLowerCase().includes(query) ||
          (role.display_name && role.display_name.toLowerCase().includes(query)) ||
          (role.description && role.description.toLowerCase().includes(query))
        );
      }
      
      // Apply sorting
      result.sort((a, b) => {
        const aValue = a[sortField.value];
        const bValue = b[sortField.value];
        
        if (sortDirection.value === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
      
      return result;
    });

    const getParentRoleName = (parentId) => {
      const parentRole = roles.value.find(role => role.id === parentId);
      return parentRole ? (parentRole.display_name || parentRole.name) : 'Unknown';
    };

    const sortBy = (field) => {
      if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortField.value = field;
        sortDirection.value = 'asc';
      }
    };

    const getSortIcon = (field) => {
      if (sortField.value !== field) return 'fa-sort';
      return sortDirection.value === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
    };

    const filterRoles = () => {
      // Implementation of filterRoles method
    };

    onMounted(() => {
      fetchRoles();
    });

    return {
      roles,
      loading,
      error,
      selectedRole,
      showRoleModal,
      isEditing,
      roleForm,
      formSubmitting,
      availablePermissions,
      loadingPermissions,
      showDeleteModal,
      roleToDelete,
      deleteSubmitting,
      expandedPermissionRoles,
      togglePermissionView,
      openCreateModal,
      openEditModal,
      closeRoleModal,
      submitRoleForm,
      confirmDelete,
      deleteRole,
      formatDate,
      filteredRoles,
      getParentRoleName,
      sortBy,
      getSortIcon,
      searchQuery,
      filterRoles
    };
  }
};
</script>

<style scoped>
.roles-table-container {
  display: flex;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.content-area {
  flex: 1;
  padding: 2rem;
  margin-left: 270px; /* Add margin to account for sidebar width */
  width: calc(100% - 270px); /* Adjust width to account for sidebar */
}

table {
  background-color: white;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 0.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
  width: 100%;
}

thead {
  background-color: #f8f9fa;
}

th {
  padding: 1rem;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #dee2e6;
}

td {
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

tr:last-child td {
  border-bottom: none;
}

.title {
  font-weight: 600;
  color: #333;
  margin-bottom: 0;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal {
  z-index: 1050;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-dialog {
  margin: 0;
  max-width: 500px;
  width: 100%;
}

.modal-content {
  margin: auto;
}

.permission-btn {
  border-radius: 4px;
  padding: 0.25rem 0.75rem;
}

.no-permissions {
  color: #6c757d;
}

.permissions-container {
  max-width: 100%;
}

.permission-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  max-height: 300px;
  overflow-y: auto;
}

.permission-chip {
  background-color: #e9ecef;
  border-radius: 16px;
  color: #495057;
  display: inline-block;
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
  padding: 0.25rem 0.75rem;
  white-space: nowrap;
}

.permission-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.expand-btn, .collapse-btn {
  padding: 0.1rem 0.5rem;
  font-size: 0.75rem;
}

.permissions-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.5rem;
}

.permissions-list .form-check {
  margin-bottom: 0.5rem;
}

/* Additional styles to fix sidebar overlay */
:deep(.sidebar) {
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 270px;
  z-index: 10;
}

.table-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.table-controls {
  width: 300px;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #f8f9fa;
}

.table th {
  font-weight: 600;
  color: #495057;
  border-top: none;
}

.table td {
  vertical-align: middle;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.input-group {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075);
}

.form-control:focus {
  box-shadow: none;
  border-color: #80bdff;
}

.badge {
  font-size: 0.85rem;
  padding: 0.35em 0.65em;
}
</style> 